import { ReactComponent as OutlineCalendar3 } from "assets/svg/outline-calendar-3.svg";
import { ReactComponent as OutlineClock2 } from "assets/svg/outline-clock-2.svg";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import formatTimeRange from "components/timeslot-popover/utils/formatTimeRange";
import messages from "messages";
import moment from "moment-timezone";
import { useMemo } from "react";
import { TIME_FORMAT_12 } from "../../../calendar-view/components/interval-editor/utils/get12hrTime";
import styles from "./BlockedIntervalTitleAndDates.module.scss";
import formatSessionDuration from "utils/formatSessionDuration";

interface BlockedIntervalTitleAndDatesProps {
  blockedInterval: CalendarBlockedInterval;
}

const BlockedIntervalTitleAndDates = ({
  blockedInterval,
}: BlockedIntervalTitleAndDatesProps) => {
  const { currentCalendarTimezone } = useCalendarTime();

  const isFullDay = useMemo(() => {
    const { start, end } = blockedInterval;
    const startMoment = moment.tz(start, currentCalendarTimezone);
    const endMoment = moment.tz(end, currentCalendarTimezone);

    return (
      startMoment.format("HH:mm") === "00:00" &&
      endMoment.format("HH:mm") === "00:00"
    );
  }, [blockedInterval, currentCalendarTimezone]);

  const diffHrs = useMemo(() => {
    const start = new Date(blockedInterval.start);
    const end = new Date(blockedInterval.end);
    const diffHrs = (+end - +start) / 1000 / 60 / 60;
    return diffHrs;
  }, [blockedInterval]);

  const blockTitle = useMemo(
    () => blockedInterval.busy_note || `(${messages.noTitle})`,
    [blockedInterval],
  );

  const startDateString = useMemo(
    () =>
      moment(blockedInterval.start)
        .tz(currentCalendarTimezone)
        .format("dddd, MMMM D "),
    [blockedInterval, currentCalendarTimezone],
  );

  const endDateString = useMemo(
    () =>
      moment(blockedInterval.end)
        .tz(currentCalendarTimezone)
        .subtract(1, "second")
        .format("dddd, MMMM D "),
    [blockedInterval, currentCalendarTimezone],
  );

  const timeRangeString = useMemo(
    () =>
      `${formatTimeRange(
        blockedInterval.start,
        blockedInterval.end,
        currentCalendarTimezone,
      )} (${formatSessionDuration(diffHrs * 60)})`,
    [blockedInterval, currentCalendarTimezone, diffHrs],
  );

  const startTimeString = useMemo(
    () =>
      moment
        .tz(blockedInterval.start, currentCalendarTimezone)
        .format(TIME_FORMAT_12),
    [blockedInterval, currentCalendarTimezone],
  );

  const endTimeString = useMemo(
    () =>
      moment
        .tz(blockedInterval.end, currentCalendarTimezone)
        .format(TIME_FORMAT_12),
    [blockedInterval, currentCalendarTimezone],
  );

  return (
    <>
      <div className={styles["block-title"]}>{blockTitle}</div>

      <hr className={styles["divider"]} />

      {startDateString === endDateString ? (
        <div className={styles["time-date-block"]}>
          <div className={styles["text-with-icon"]}>
            <OutlineCalendar3 />
            {startDateString}
          </div>
          {!isFullDay && (
            <div className={styles["text-with-icon"]}>
              <OutlineClock2 />
              {timeRangeString}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={styles["section__title"]}>{messages.from}</div>
          <div className={styles["time-date-block"]}>
            <div className={styles["text-with-icon"]}>
              <OutlineCalendar3 />
              {startDateString}
            </div>
            {!isFullDay && (
              <div className={styles["text-with-icon"]}>
                <OutlineClock2 />
                {startTimeString}
              </div>
            )}
          </div>
          <div className={styles["section__title"]}>
            {messages.to__capitalized}
          </div>
          <div className={styles["time-date-block"]}>
            <div className={styles["text-with-icon"]}>
              <OutlineCalendar3 />
              {endDateString}
            </div>
            {!isFullDay && (
              <div className={styles["text-with-icon"]}>
                <OutlineClock2 />
                {endTimeString}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default BlockedIntervalTitleAndDates;
